import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import { Container, Typography, Box, Button } from '@mui/material';
import { ArrowBack, Launch, GitHub } from '@mui/icons-material';
import { getProjectBySlug } from '../utils/projects';
import { getPostsByTag } from '../utils/blog';
import MarkdownContent from '../components/MarkdownContent';

const useStyles = makeStyles((theme) => ({
  backLink: {
    marginBottom: theme.spacing(3),
  },
  title: {
    fontWeight: 700,
    marginBottom: theme.spacing(1),
  },
  summary: {
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(3),
  },
  links: {
    display: 'flex',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(4),
  },
  relatedPosts: {
    marginBottom: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  relatedPostLink: {
    color: theme.palette.secondary.main,
    textDecoration: 'none',
    fontSize: '0.875rem',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
}));

const ProjectDetailPage = () => {
  const classes = useStyles();
  const { slug } = useParams();
  const project = getProjectBySlug(slug);

  if (!project) {
    return (
      <Container maxWidth="md">
        <Box sx={{ py: 6 }}>
          <Typography variant="h4" gutterBottom>
            Project not found
          </Typography>
          <Button component={Link} to="/projects" startIcon={<ArrowBack />}>
            Back to projects
          </Button>
        </Box>
      </Container>
    );
  }

  const relatedPosts = getPostsByTag(project.tag);

  return (
    <Container maxWidth="md">
      <Box sx={{ py: 6 }}>
        <Button
          component={Link}
          to="/projects"
          startIcon={<ArrowBack />}
          className={classes.backLink}
        >
          Back to projects
        </Button>

        <Typography variant="h2" className={classes.title}>
          {project.title}
        </Typography>

        <Typography variant="body1" className={classes.summary}>
          {project.summary}
        </Typography>

        <Box className={classes.links}>
          {project.codeLink && project.codeLink !== '#' && (
            <Button
              variant="outlined"
              href={project.codeLink}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<GitHub />}
            >
              Code
            </Button>
          )}
          {project.demoLink && project.demoLink !== '#' && (
            <Button
              variant="outlined"
              href={project.demoLink}
              target="_blank"
              rel="noopener noreferrer"
              startIcon={<Launch />}
            >
              Live Site
            </Button>
          )}
        </Box>

        {relatedPosts.length > 0 && (
          <Box className={classes.relatedPosts}>
            <Typography
              variant="subtitle2"
              style={{ fontWeight: 600, marginBottom: 8 }}
            >
              Related Posts:
            </Typography>
            {relatedPosts.map((post) => (
              <Box key={post.slug} sx={{ mb: 0.5 }}>
                <Link
                  to={`/blog/${post.slug}`}
                  className={classes.relatedPostLink}
                >
                  {post.title}
                </Link>
              </Box>
            ))}
          </Box>
        )}

        <MarkdownContent body={project.body} />
      </Box>
    </Container>
  );
};

export default ProjectDetailPage;
